import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// Where the two halves of the logo sit on the file's own canvas, in its user units. The file
// stacks the wordmark under the mark; the side-by-side form crops each out of the same file
// rather than keeping a second drawing of either.
const CANVAS = { width: 1254, height: 1175 };
const MARK = { x: 293, y: 66, width: 756, height: 756 };
const WORDMARK = { x: 131, y: 835, width: 993, height: 280 };

/// One region of the logo file, shown at a given height by moving the whole file under a
/// window the size of that region.
function Crop({ src, region, height }: { src: string; region: typeof MARK; height: number }) {
  const scale = height / region.height;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        width: region.width * scale,
        height,
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          position: 'absolute',
          // Physical, not logical: the crop is of a picture, which does not turn round in a
          // right-to-left layout.
          left: -region.x * scale,
          top: -region.y * scale,
          width: CANVAS.width * scale,
          height: CANVAS.height * scale,
          maxWidth: 'none',
          display: 'block',
        }}
      />
    </Box>
  );
}

/// The Emiro logo. Served as a file rather than drawn here: it is a brand asset and there
/// should be exactly one of it.
///
/// Two files, one logo: the wordmark under the mark is near black, which disappears on a dark
/// interface, so the dark version carries it in light grey. They differ in nothing else, and
/// neither carries the provenance manifest any more - it signs the bytes of the original, and
/// the original is the copy in the repository root.
///
/// Stacked is the logo as drawn. Inline sets the wordmark beside the mark, the way the website
/// header does, for a bar or a sidebar where height is what there is least of; mark is the mark
/// alone. The glow is the website's: the mark lit from within, for a screen it stands alone on.
export default function EmiroLogo({
  width = 150,
  layout = 'stacked',
  size = 28,
  glow = false,
}: {
  width?: number;
  layout?: 'stacked' | 'inline' | 'mark';
  /// The height of the mark, for the inline and mark layouts.
  size?: number;
  glow?: boolean;
}) {
  const theme = useTheme();
  const src = theme.palette.mode === 'dark' ? '/emiro-logo-dark.svg' : '/emiro-logo.svg';
  const filter = glow ? `drop-shadow(0 0 ${Math.round(size * 0.35)}px rgba(19, 216, 210, 0.55))` : undefined;

  if (layout === 'stacked') {
    return <Box component="img" src={src} alt="Emiro" sx={{ width, flexShrink: 0, display: 'block', filter }} />;
  }

  return (
    <Box
      role="img"
      aria-label="Emiro"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: `${size * 0.32}px`, flexShrink: 0, direction: 'ltr' }}
    >
      <Box sx={{ filter }}>
        <Crop src={src} region={MARK} height={size} />
      </Box>
      {/* 280 units tall with a 243-unit capital: the capitals end up about half the mark. */}
      {layout === 'inline' && <Crop src={src} region={WORDMARK} height={size * 0.6} />}
    </Box>
  );
}
